import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useController, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';

import {
  Button,
  FormField,
  Input,
  Select,
  useDialogContext,
} from '@components/primitives';

import { labelForPlan } from '@components/features/Settings/TeamBilling/utils';
import { api } from '@services/apiService';
import { adminTeamPayments } from '@utils/resources/admin';

import { AdminTeamObject } from './types';

const STRIPE_CUSTOMER_ID_REGEX = /^cus_[A-Za-z0-9]{8,}$/;

const schema = z.object({
  tier: z.union([
    z.literal('free'),
    z.literal('pro'),
    z.literal('enterprise'),
    z.literal('legacy'),
  ]),
  stripeCustomerId: z
    .union([
      // allow empty string
      z
        .string()
        .trim()
        .max(0)
        .transform((id) => id || undefined),
      // allow stripe customer id
      z.string().regex(STRIPE_CUSTOMER_ID_REGEX),
    ])
    .nullable()
    .optional(),
});

function useUpdateTeamPayments(uuid: string) {
  return useMutation({
    mutationFn: (values: z.infer<typeof schema>) => {
      const payload = {
        tier: values.tier,
        ...(values.stripeCustomerId
          ? { stripeCustomerId: values.stripeCustomerId }
          : {}),
      };

      return api.put(adminTeamPayments(uuid), { json: payload }).json();
    },
  });
}

export const TeamPaymentUpdate = ({ team }: { team: AdminTeamObject }) => {
  const dialog = useDialogContext();
  const queryClient = useQueryClient();
  const mutation = useUpdateTeamPayments(team.uuid);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      tier: team.tier,
      stripeCustomerId: team.stripeCustomerId || '',
    },
  });

  const tierController = useController({
    name: 'tier',
    control: form.control,
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values);
      toast.success('Team payments updated');
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'teams', team.uuid],
      });
      dialog.close();
    } catch {
      toast.error('Failed to update team payments');
    }
  });

  return (
    <form onSubmit={handleSubmit}>
      <FormField label="Tier" className="mb-5">
        <Select
          value={tierController.field.value}
          onChange={tierController.field.onChange}
        >
          <Select.Option value="free">{labelForPlan('free')}</Select.Option>
          <Select.Option value="pro">{labelForPlan('pro')}</Select.Option>
          <Select.Option value="enterprise">
            {labelForPlan('enterprise')}
          </Select.Option>
        </Select>
      </FormField>

      {tierController.field.value !== 'free' && (
        <FormField
          label="Stripe Customer ID"
          className="mb-5"
          error={form.formState.errors.stripeCustomerId?.message}
        >
          <Input
            {...form.register('stripeCustomerId')}
            placeholder="Stripe Customer ID"
            autoComplete="off"
          />
        </FormField>
      )}

      <Button variant="primary" loading={form.formState.isSubmitting}>
        Update team
      </Button>
    </form>
  );
};
